import { AiApi } from '@/etc/api'
import { fromStorage, toStorage } from '@/etc/storage'
import { liquify } from '@/etc/liquid'
import supabase from '@/etc/supabase'
import { LOG } from '@/etc/log'
import { factory } from '@/store/project'
import { jclone, blob2base64 } from '@/etc/utils'
import { isProbablyUrl } from '@/etc/ai.workflowInputs'
import {
  AUDIO_GENERATION_INPUT_KEY,
  AUDIO_MODE_INPUT_KEY,
  normalizeAudioMode,
  resolveAudioGenerationCapability,
} from '@/etc/aiAudioMode'

const { VUE_APP_S3 } = process.env

const log = LOG.m('store::ai')
//const IMAGES_VIDEOS_URI_SEND_BASE64 = false

// Phase 1 (Node Canvas): auto-import provider outputs into Library as real assets.
// This ensures downstream nodes can reference generated media by UUID like any other asset upload.
const __vybyAutoImportImportedFids = new Set()
const __vybyAutoImportInflight = new Map()

function applyAudioGenerationRuntimeInput(flow, requestInputs) {
  const capability = resolveAudioGenerationCapability(flow)
  if (
    !Object.prototype.hasOwnProperty.call(requestInputs, AUDIO_MODE_INPUT_KEY) ||
    !capability.supported ||
    !capability.providerField ||
    !capability.taskType
  ) {
    return
  }

  if (!flow.inputs || typeof flow.inputs !== 'object') flow.inputs = {}
  flow.inputs[AUDIO_GENERATION_INPUT_KEY] = {
    name: 'Generate audio',
    type: 'text',
    display: 'hidden',
    links: {
      __vyby_audio_runtime_link: {
        node: capability.taskType,
        input: capability.providerField,
      },
    },
  }
  requestInputs[AUDIO_GENERATION_INPUT_KEY] =
    normalizeAudioMode(requestInputs[AUDIO_MODE_INPUT_KEY]) !== 'no_audio'
}

function s3Base() {
  return (VUE_APP_S3 || '').trim().replace(/\/+$/, '')
}

function getOutFid(out) {
  if (!out) return ''
  const fid =
    typeof out.fid === 'string'
      ? out.fid
      : typeof out.fid === 'number'
        ? `${out.fid}`
        : ''
  return `${fid || ''}`.trim()
}

function getOutFilePath(out) {
  if (!out) return ''
  const direct =
    typeof out.filePath === 'string'
      ? out.filePath
      : typeof out.file_path === 'string'
        ? out.file_path
        : typeof out?.out?.s3 === 'string'
          ? out.out.s3
          : typeof out?.out?.filePath === 'string'
            ? out.out.filePath
            : typeof out?.out?.file_path === 'string'
              ? out.out.file_path
              : ''
  return `${direct || ''}`.trim()
}

function guessMimeFromOut(out, blob) {
  const bt = typeof blob?.type === 'string' ? blob.type.trim() : ''
  if (bt) return bt
  const t =
    typeof out?.type === 'string'
      ? out.type
      : typeof out?.mime === 'string'
        ? out.mime
        : typeof out?.out?.type === 'string'
          ? out.out.type
          : ''
  return `${t || ''}`.trim()
}

function buildS3DownloadUrl(filePath) {
  const fp = `${filePath || ''}`.trim()
  if (!fp) return ''
  if (/^https?:\/\//i.test(fp)) return fp
  const base = s3Base()
  if (!base) return ''
  return `${base}/${fp.replace(/^\/+/, '')}`
}

function fileNameFromPath(filePath, fid) {
  const fp = `${filePath || ''}`.trim()
  if (!fp) return fid ? `vyby-${fid}` : `vyby-${Date.now()}`
  const name = fp.split('/').pop()
  return (name || '').trim() || (fid ? `vyby-${fid}` : `vyby-${Date.now()}`)
}

async function importOutputToLibrary(store, out) {
  const fid = getOutFid(out)
  const filePath = getOutFilePath(out)
  const url = buildS3DownloadUrl(filePath)

  if (!fid || !filePath || !url) {
    return { ok: false, fid, filePath, reason: 'missing fid/filePath/url' }
  }

  const projectLib = store?.rootState?.project?.library || null
  if (projectLib && projectLib[fid]) {
    __vybyAutoImportImportedFids.add(fid)
    return { ok: true, fid, filePath, skipped: true, reason: 'already in library' }
  }

  if (__vybyAutoImportImportedFids.has(fid)) {
    return { ok: true, fid, filePath, skipped: true, reason: 'already imported (session)' }
  }

  if (__vybyAutoImportInflight.has(fid)) {
    return __vybyAutoImportInflight.get(fid)
  }

  const p = (async () => {
    try {
      if (typeof fetch !== 'function') {
        return { ok: false, fid, filePath, reason: 'fetch not available' }
      }

      log.info('[autoImport] fetching output', { fid, filePath, url })
      const resp = await fetch(url)
      if (!resp || !resp.ok) {
        return { ok: false, fid, filePath, reason: `fetch failed (${resp?.status || 'unknown'})` }
      }

      const blob = await resp.blob()
      const name = fileNameFromPath(filePath, fid)
      const mime = guessMimeFromOut(out, blob) || 'application/octet-stream'

      let file = null
      try {
        if (typeof File !== 'undefined') {
          file = new File([blob], name, { type: mime })
        } else {
          // Best-effort fallback (Blob is accepted by our addMedia pipeline; name is used for meta).
          blob.name = name
          file = blob
        }
      } catch (e) {
        try {
          blob.name = name
        } catch (e2) { /* noop */ }
        file = blob
      }

      // `addS3Media` will set file.uuid = out.fid, but keep this for traceability.
      try {
        file.uuid = fid
      } catch (e) { /* noop */ }

      await store.dispatch(
        'project/addS3Media',
        { file, out: { ...(out || {}), fid, filePath } },
        { root: true }
      )

      __vybyAutoImportImportedFids.add(fid)
      return { ok: true, fid, filePath }
    } catch (e) {
      return { ok: false, fid, filePath, reason: e?.message || 'import failed' }
    } finally {
      __vybyAutoImportInflight.delete(fid)
      try {
        // No `typeof process` guard — webpack 5 has no runtime `process`;
        // DefinePlugin inlines NODE_ENV as a literal.
        const __isDev = process.env.NODE_ENV !== 'production'
        if (__isDev && typeof window !== 'undefined') {
          window.__vybyAutoImportOutputs = {
            importedCount: __vybyAutoImportImportedFids.size,
            inflightCount: __vybyAutoImportInflight.size,
          }
        }
      } catch (e) { /* noop */ }
    }
  })()

  __vybyAutoImportInflight.set(fid, p)
  return p
}

async function autoImportTaskOutputsToLibrary(store, rawOutput, { taskId = null } = {}) {
  const outputs = normalizeTaskOutput(rawOutput)
  if (!outputs.length) return []

  const candidates = outputs.filter((o) => !!getOutFid(o) && !!getOutFilePath(o))
  if (!candidates.length) return []

  log.info('[autoImport] candidate outputs', {
    taskId,
    count: candidates.length,
    fids: candidates.map((o) => getOutFid(o)).filter(Boolean),
  })

  const results = await Promise.all(candidates.map((o) => importOutputToLibrary(store, o)))

  try {
    const __isDev = process.env.NODE_ENV !== 'production'
    if (__isDev && typeof window !== 'undefined') {
      window.__vybyAutoImportOutputs = {
        ...(window.__vybyAutoImportOutputs || {}),
        lastTaskId: taskId,
        lastResults: results,
        importedCount: __vybyAutoImportImportedFids.size,
        inflightCount: __vybyAutoImportInflight.size,
      }
    }
  } catch (e) { /* noop */ }

  return results
}

function isSingleOutputRecord(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false
  // Task outputs commonly look like:
  // { filePath, fileName, mime, type, uuid, fid, ... }
  // or { url, type, ... }
  // or { out: { s3, type, ... }, ... }
  const has = (k) => Object.prototype.hasOwnProperty.call(obj, k)
  if (
    has('filePath') ||
    has('file_path') ||
    has('fileName') ||
    has('file_name') ||
    has('filename') ||
    has('fid') ||
    has('mime') ||
    has('type') ||
    has('url') ||
    has('blob')
  ) {
    return true
  }
  const out = obj.out
  if (out && typeof out === 'object' && !Array.isArray(out)) {
    if (
      typeof out.s3 === 'string' ||
      typeof out.filePath === 'string' ||
      typeof out.file_path === 'string' ||
      typeof out.url === 'string'
    ) {
      return true
    }
  }
  return false
}

function normalizeTaskOutput(output) {
  if (!output) return []

  // Common shapes:
  // - array of outputs
  // - object map of outputs
  // - single output object
  const arr = Array.isArray(output)
    ? output
    : typeof output === 'object'
      ? isSingleOutputRecord(output)
        ? [output]
        : Object.values(output)
      : [output]

  // Sometimes providers/handlers wrap outputs an extra level (e.g. [ [ ... ] ]).
  const flattened = arr.flat ? arr.flat(1) : [].concat(...arr)

  return flattened.filter(Boolean)
}

function extractTaskOutputForUuid(rawOutput, uuid) {
  if (!rawOutput) return rawOutput
  // Some payloads are shaped like { [taskId]: output } for multi-task broadcasts.
  if (
    uuid &&
    rawOutput &&
    typeof rawOutput === 'object' &&
    !Array.isArray(rawOutput) &&
    Object.prototype.hasOwnProperty.call(rawOutput, uuid)
  ) {
    return rawOutput[uuid]
  }
  return rawOutput
}

function firstInputLink(inputDef) {
  const links = inputDef?.links && typeof inputDef.links === 'object' ? inputDef.links : null
  if (!links) return null
  const firstKey = Object.keys(links)[0]
  if (!firstKey) return null
  const link = links[firstKey]
  return link && typeof link === 'object' ? link : null
}

function shouldPreserveLumaImageToVideoRef(flow, key) {
  const provider = `${flow?.api_name || flow?.provider || ''}`.trim().toLowerCase()
  if (provider !== 'lumalabs') return false
  const inputDef =
    flow && flow.inputs && typeof flow.inputs === 'object' ? flow.inputs[key] : null
  if (!inputDef || inputDef.type !== 'image') return false
  const link = firstInputLink(inputDef)
  const node = `${link?.node || ''}`.trim()
  const input = `${link?.input || ''}`.trim()
  if (node !== 'image-to-video') return false
  return input === 'frame0_image' || input === 'frame1_image'
}

function shouldPreserveProviderMediaRef(flow, key) {
  const provider = `${flow?.api_name || flow?.provider || ''}`.trim().toLowerCase()
  if (!['piapi', 'runwayml'].includes(provider)) return false
  const inputDef =
    flow && flow.inputs && typeof flow.inputs === 'object' ? flow.inputs[key] : null
  return ['image', 'images', 'video', 'videos', 'audio', 'audios'].includes(
    `${inputDef?.type || ''}`.toLowerCase()
  )
}

function looksLikeUuid(value) {
  if (typeof value !== 'string') return false
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value.trim()
  )
}

function extFromMime(mime) {
  const m = `${mime || ''}`.trim().toLowerCase()
  if (!m) return ''
  if (m === 'image/jpeg') return 'jpg'
  if (m === 'image/png') return 'png'
  if (m === 'image/webp') return 'webp'
  if (m === 'image/gif') return 'gif'
  if (m === 'video/mp4') return 'mp4'
  if (m === 'video/webm') return 'webm'
  if (m === 'audio/mpeg') return 'mp3'
  if (m === 'audio/wav' || m === 'audio/x-wav') return 'wav'
  if (m === 'audio/ogg') return 'ogg'
  return ''
}

function extFromName(name) {
  const n = `${name || ''}`.trim()
  if (!n || !n.includes('.')) return ''
  const ext = n.split('.').pop().toLowerCase()
  return ext || ''
}

function resolveAssetExt(asset, mime) {
  const fromMeta =
    extFromName(asset?._asset?.meta?.name) ||
    extFromName(asset?.meta?.name) ||
    extFromName(asset?._asset?.name)
  if (fromMeta) return fromMeta
  const fromMime = extFromMime(mime)
  if (fromMime) return fromMime
  return 'bin'
}

async function ensureSingleUuidPersisted(store, uuid, { projectId, userId }) {
  const fid = `${uuid || ''}`.trim()
  if (!looksLikeUuid(fid)) return null

  const existing = await supabase.from('files').select('id, path').eq('id', fid).maybeSingle()
  if (existing.data && existing.data.path) return existing.data

  const asset = factory.asset(fid)
  const blob = asset?._asset?.blob || null
  const mime = `${asset?._asset?.mime || blob?.type || ''}`.trim()
  if (!asset || !blob || !mime) {
    throw new Error(
      `Asset ${fid} is not persisted and cannot be uploaded (missing local blob/mime)`
    )
  }
  if (!userId) {
    throw new Error('Cannot persist asset before generate: missing user id')
  }
  if (!projectId) {
    throw new Error('Cannot persist asset before generate: missing project id')
  }

  const ext = resolveAssetExt(asset, mime)
  const relName = `${projectId}/${fid}.${ext}`
  const dbPath = `${userId}/${relName}`

  const uploadedUrl = await store.dispatch(
    'user/uploadFile',
    { file: blob, name: relName },
    { root: true }
  )
  if (!uploadedUrl) {
    throw new Error(`Failed to upload asset ${fid} before generate`)
  }

  const row = {
    id: fid,
    owner: userId,
    project: projectId,
    path: dbPath,
    mime,
    size: blob.size || 0,
    onwall: true,
  }
  const saved = await supabase.from('files').upsert(row).select()
  if (saved.error) {
    throw new Error(`Failed to persist files row for ${fid}: ${saved.error.message}`)
  }
  return row
}

async function ensureUuidReferencesPersisted(store, inputs) {
  const userId = `${store?.rootState?.user?.uid || ''}`.trim()
  const projectId = `${store?.rootState?.project?.uuid || ''}`.trim()
  const refs = new Set()
  Object.keys(inputs || {}).forEach((key) => {
    const value = inputs?.[key]
    if (typeof value === 'string') {
      const raw = value.trim()
      if (looksLikeUuid(raw)) refs.add(raw)
      return
    }
    if (Array.isArray(value)) {
      value.forEach((item) => {
        const uri = `${item?.uri || ''}`.trim()
        if (looksLikeUuid(uri)) refs.add(uri)
        const url = `${item?.url || ''}`.trim()
        if (looksLikeUuid(url)) refs.add(url)
      })
      return
    }
    if (value && typeof value === 'object') {
      const uri = `${value?.uri || ''}`.trim()
      if (looksLikeUuid(uri)) refs.add(uri)
      const url = `${value?.url || ''}`.trim()
      if (looksLikeUuid(url)) refs.add(url)
    }
  })
  if (!refs.size) return

  for (const fid of refs) {
    await ensureSingleUuidPersisted(store, fid, { projectId, userId })
    log.info('[generate-preflight] persisted missing input reference asset', {
      uuid: fid,
      projectId,
    })
  }
}

export default {
  namespaced: true,
  state: {
    on: fromStorage('project/ai/on', false),
    quiz: fromStorage('project/quiz', ''),
    gens: fromStorage('project/gens', {}),
    tasks: {},
    project: {
      env: fromStorage('project/env', {}),
      frame: fromStorage('project/frame', {}),
    },
    tools: [],
    toolImg: {},
    vybyHistory: [],
    status: [],
  },
  getters: {
    jwt: (state, getters, rootState) => {
      return rootState.user.jwt
    },
  },
  mutations: {
    on: (state, next) => {
      state.on = next
      toStorage('project/ai/on', next)
    },
    quiz: (state, next) => {
      state.quiz = next
      toStorage('project/quiz', next)
    },
    tools: (state, next) => {
      state.tools = next
    },
    toolImg: (state, next) => {
      state.toolImg[next.id] = URL.createObjectURL(next.img)
    },
    project: (state, { env, frame }) => {
      state.project.env = env
      state.project.frame = frame
      toStorage('project/env', env)
      toStorage('project/frame', frame)
    },
    addTask: (state, task) => {
      log.info('addTask', task)
      state.tasks[task.uuid] = {
        uuid: task.uuid,
        flow: task.flow || null,
        processing: true,
        complete: false,
        failed: false,
        progress: 0,
        stage: 'queued',
        output: null,
        error: null,
        timestamp: new Date().toISOString(),
      }
    },
    taskQueued: (state, { uuid, output }) => {
      log.info('taskQueued', { uuid, output })
      state.tasks[uuid] = {
        ...state.tasks[uuid],
        processing: true,
        stage: 'queued',
        timestamp: new Date().toISOString(),
      }
    },
    taskStart: (state, { uuid, output }) => {
      log.info('taskStart', { uuid, output })
      state.tasks[uuid] = {
        ...state.tasks[uuid],
        processing: true,
        stage: 'started',
        timestamp: new Date().toISOString(),
      }
    },
    taskProgress: (state, { uuid, progress, stage, timestamp }) => {
      log.info('taskProgress', { uuid, progress, stage, timestamp })
      state.tasks[uuid] = {
        ...state.tasks[uuid],
        progress,
        stage,
        timestamp,
      }
    },
    // default complete handler
    taskComplete: (state, { uuid, output, inputs, timestamp }) => {
      log.info('taskComplete', { uuid, output, inputs, timestamp })
      const normalizedOutput = normalizeTaskOutput(output)
      state.tasks[uuid] = {
        ...state.tasks[uuid],
        processing: false,
        complete: true,
        failed: normalizedOutput.length === 0,
        output: normalizedOutput,
        inputs,
        error:
          normalizedOutput.length === 0
            ? { message: 'No valid outputs generated' }
            : null,
        stage: 'complete',
        timestamp,
      }
      console.log('taskComplete norm', {
        state: state.tasks[uuid],
        output: normalizedOutput,
        s3: process.env.VUE_APP_S3,
      })
    },
    /*
    taskComplete_cancel: (state, { uuid, output, timestamp }) => {
      log.info('taskComplete', { uuid, output, timestamp })
      log.debug('Raw output structure:', JSON.stringify(output, null, 2))

      // Нормализуем output в массив
      const normalizedOutput =
        output && typeof output === 'object' && !Array.isArray(output)
          ? Object.values(output)
          : Array.isArray(output)
          ? output
          : output
          ? [output]
          : []

      log.debug('Normalized output:', JSON.stringify(normalizedOutput, null, 2))

      // Обновляем состояние задачи
      state.tasks[uuid] = {
        ...state.tasks[uuid],
        processing: false,
        complete: true,
        failed: !normalizedOutput.length,
        output: normalizedOutput,
        error: !normalizedOutput.length
          ? { message: 'No valid outputs generated' }
          : null,
        stage: 'complete',
        timestamp,
      }

      // Обрабатываем выходные данные
      normalizedOutput.forEach((out, index) => {
        try {
          if (out.blob && typeof out.blob === 'string') {
            // Проверяем, является ли blob валидной строкой base64
            if (/^[A-Za-z0-9+/=]+$/.test(out.blob)) {
              const binary = atob(out.blob)
              const array = new Uint8Array(binary.length)
              for (let i = 0; i < binary.length; i++) {
                array[i] = binary.charCodeAt(i)
              }
              out.url = URL.createObjectURL(
                new Blob([array], { type: out.out.type })
              )
              log.debug(`Generated URL for blob output ${index}: ${out.url}`)
            } else {
              log.warn(`Invalid base64 string for output ${index}:`, out.blob)
              out.url = null
            }
          } else if (out.out.s3) {
            // Формируем S3 URL
            out.url = `${VUE_APP_S3}/${out.out.s3}`
            log.debug(`Generated S3 URL for output ${index}: ${out.url}`)
          } else {
            log.warn(`No valid blob or S3 path for output ${index}`)
            out.url = null
          }
        } catch (error) {
          log.err(`Error processing output ${index}:`, error)
          out.url = null
        }
      })
    },
    taskComplete_1_1_1: (state, { uuid, output, timestamp }) => {
      log.info('taskComplete', { uuid, output, timestamp })
      log.debug('Raw output structure:', JSON.stringify(output, null, 2))

      const normalizedOutput =
        output && typeof output === 'object' && !Array.isArray(output)
          ? Object.values(output)
          : Array.isArray(output)
          ? output
          : output
          ? [output]
          : []

      log.debug('Normalized output:', JSON.stringify(normalizedOutput, null, 2))

      state.tasks[uuid] = {
        ...state.tasks[uuid],
        processing: false,
        complete: true,
        failed: !normalizedOutput.length,
        output: normalizedOutput,
        error: !normalizedOutput.length
          ? { message: 'No valid outputs generated' }
          : null,
        stage: 'complete',
        timestamp,
      }

      normalizedOutput.forEach((out) => {
        if (out.blob) {
          const binary = atob(out.blob)
          const array = new Uint8Array(binary.length)
          for (let i = 0; i < binary.length; i++) {
            array[i] = binary.charCodeAt(i)
          }
          out.url = URL.createObjectURL(
            new Blob([array], { type: out.out.type })
          )
        } else if (out.out.s3) {
          out.url = `${process.env.VUE_APP_S3}/${out.out.s3}`
        }
      })
    },
    taskComplete___: (state, { uuid, output, timestamp }) => {
      log.info('taskComplete', { uuid, output, timestamp })
      log.debug('Raw output structure:', JSON.stringify(output, null, 2))

      // Преобразуем объект output в массив
      const normalizedOutput =
        output && typeof output === 'object' && !Array.isArray(output)
          ? Object.values(output)
          : Array.isArray(output)
          ? output
          : output
          ? [output]
          : []

      log.debug('Normalized output:', JSON.stringify(normalizedOutput, null, 2))

      state.tasks[uuid] = {
        ...state.tasks[uuid],
        processing: false,
        complete: true,
        failed: !normalizedOutput.length,
        output: normalizedOutput,
        error: !normalizedOutput.length
          ? { message: 'No valid outputs generated' }
          : null,
        stage: 'complete',
        timestamp,
      }

      // Обрабатываем blob (base64) или s3 URL
      normalizedOutput.forEach((out) => {
        if (IMAGES_VIDEOS_URI_SEND_BASE64 && out.blob) {
          // Преобразуем base64 в Blob и создаем URL
          const binary = atob(out.blob)
          const array = new Uint8Array(binary.length)
          for (let i = 0; i < binary.length; i++) {
            array[i] = binary.charCodeAt(i)
          }
          out.url = URL.createObjectURL(
            new Blob([array], { type: out.out.type })
          )
        } else if (out.out.s3) {
          // Используем S3 URL
          out.url = `${process.env.VUE_APP_S3}/${out.out.s3}`
          out.test = process.env.VUE_APP_S3 + ' vvss ' + process.env.VUE_APP_S3
        }
        console.log('normazol', process.env.VUE_APP_S3, process.env.S3_URL)
      })
    },
    taskComplete_old: (state, { uuid, output, timestamp }) => {
      log.info('taskComplete', { uuid, output, timestamp })
      state.tasks[uuid] = {
        ...state.tasks[uuid],
        processing: false,
        complete: true,
        failed: !output,
        output: Array.isArray(output) ? output : output ? [output] : [],
        error: !output ? { message: 'No valid outputs generated' } : null,
        stage: 'complete',
        timestamp,
      }
    },
    */
    taskError: (state, { uuid, error, timestamp }) => {
      log.info('taskError', { uuid, error, timestamp })
      state.tasks[uuid] = {
        ...state.tasks[uuid],
        processing: false,
        complete: false,
        failed: true,
        error,
        stage: 'taskError',
        timestamp,
      }
    },
    helpMessage: (state, { role, message }) => {
      state.vybyHistory.push({ role, message })
    },
    status: (state, next) => {
      state.status = next
    },
  },
  actions: {
    init: async (store) => {
      log.info('init')
      await store.dispatch('loadTools')
      await store.dispatch('initws')
      log.info('ai init complete')
    },
    backStatus: async (store, { data }) => {
      store.commit('status', data || [])
    },
    initws: async (store) => {
      const socket = store.rootState.io._io.core
      if (!socket) {
        log.warn('core io not exists, retrying in 5s')
        setTimeout(() => {
          store.dispatch('initws')
        }, 5000)
        return
      }
      log.info('WebSocket initialized:', socket.id)
      socket.on('connect', () => {
        log.info('WebSocket connected:', socket.id)
      })
      socket.on('disconnect', () => {
        log.warn('WebSocket disconnected')
      })
      socket.on('ai:status', (data) => {
        if (data.length) {
          log.info('ai:status', data)
          store.dispatch('bctask', { data })
        }
      })
    },
    calibrate: (store, info) => {
      store.commit('project', {
        env: info.environment,
        frame: info.picture,
      })
    },
    loadTools: async (store) => {
      const { error, data } = await supabase
        .from('flows')
        .select()
        .eq('enabled', true)

      if (error || !Array.isArray(data)) {
        log.err('loadTools error', error, data)
        return
      }
      const tools = data
        .map((flow) => {
          if (!flow.image) return null
          return {
            ...flow,
            img: flow.image,
          }
        })
        .filter((x) => !!x)
      store.commit('tools', tools)
    },
    callTool: async (store, data) => {
      log.info('@callTool', data)
      const f = store.state.tools.find((t) => t.id === data.id)
      if (!f) {
        log.err('Tool not found', data.id)
        return null
      }
      const flow = jclone(f)
      const requestInputs = { ...(data.inputs || {}) }
      applyAudioGenerationRuntimeInput(flow, requestInputs)
      log.info('Flow found:', flow)
      const inputs = {}
      const assets = []
      Object.keys(flow.inputs).forEach((key) => {
        log.info(`Processing input ${key}:`, requestInputs[key])
        if (['text', 'number', 'enum'].includes(flow.inputs[key].type)) {
          inputs[key] = requestInputs[key]
          log.info(`Input ${key} copied as is:`, inputs[key])
        } else if (['image', 'video', 'audio'].includes(flow.inputs[key].type)) {
          const inputValue = requestInputs[key]
          if (shouldPreserveProviderMediaRef(flow, key)) {
            if (typeof inputValue === 'string') {
              inputs[key] = inputValue
              log.info(`Input ${key} preserved as provider media reference`)
            } else if (inputValue && typeof inputValue?.uri === 'string') {
              inputs[key] = inputValue.uri
              log.info(`Input ${key} preserved as provider media object URI`)
            } else {
              log.warn(`Invalid provider media reference value for ${key}:`, inputValue)
            }
            return
          }
          // Luma image-to-video expects frame URLs. Preserve UUID/URL for backend resolution.
          if (shouldPreserveLumaImageToVideoRef(flow, key)) {
            if (typeof inputValue === 'string') {
              inputs[key] = inputValue
              log.info(`Input ${key} preserved as reference for Luma image-to-video`)
            } else if (inputValue && typeof inputValue?.uri === 'string') {
              inputs[key] = inputValue.uri
              log.info(
                `Input ${key} preserved as reference object URI for Luma image-to-video`
              )
            } else {
              log.warn(`Invalid preserved reference value for ${key}:`, inputValue)
            }
            return
          }
          if (typeof inputValue === 'string') {
            if (isProbablyUrl(inputValue)) {
              // If a real URL was provided, pass through unchanged.
              inputs[key] = inputValue
              log.info(`Input ${key} is URL:`, inputValue)
            } else {
              // Otherwise treat string as an asset UUID (avoids UUID-vs-URL ambiguity).
              const asset = factory.asset(inputValue)
              if (asset && asset._asset.mime && asset._asset.blob) {
                const file = blob2base64(
                  asset._asset.blob,
                  asset._asset.mime
                ).then((base64) => ({ base64, input: key }))
                assets.push(file)
                log.info(`Input ${key} is UUID, converting to base64`)
              } else {
                log.warn(`Invalid asset UUID for input ${key}:`, inputValue)
              }
            }
          } else {
            const asset = factory.asset(inputValue)
            if (asset && asset._asset.mime && asset._asset.blob) {
              const file = blob2base64(
                asset._asset.blob,
                asset._asset.mime
              ).then((base64) => ({ base64, input: key }))
              assets.push(file)
              log.info(`Input ${key} is asset, converting to base64`)
            } else {
              log.warn(`Invalid asset for input ${key}:`, inputValue)
            }
          }
        } else if (['images', 'videos', 'audios'].includes(flow.inputs[key].type)) {
          if (Array.isArray(requestInputs[key])) {
            requestInputs[key].forEach((item, index) => {
              const normalized =
                typeof item === 'string' ? { uri: item } : item && item.uri ? item : null

              if (!normalized || !normalized.uri) return
              const uri = normalized.uri

              if (shouldPreserveProviderMediaRef(flow, key)) {
                if (!inputs[key]) inputs[key] = []
                inputs[key].push({
                  uri,
                  tag: normalized.tag,
                  position: normalized.position,
                })
                log.info(`Input ${key}[${index}] preserved as provider media reference:`, uri)
                return
              }

              if (isProbablyUrl(uri)) {
                if (!inputs[key]) inputs[key] = []
                inputs[key].push({
                  uri,
                  tag: normalized.tag,
                  position: normalized.position,
                })
                log.info(`Input ${key}[${index}] is URL:`, uri)
                return
              }

              const asset = factory.asset(uri)
              if (asset && asset._asset.mime && asset._asset.blob) {
                const file = blob2base64(
                  asset._asset.blob,
                  asset._asset.mime
                ).then((base64) => ({
                  base64,
                  uri,
                  input: key,
                  index,
                  tag: normalized.tag,
                  position: normalized.position,
                }))
                assets.push(file)
                log.info(`Input ${key}[${index}] is UUID, converting to base64`)
              } else {
                log.warn(`Invalid asset UUID for input ${key}[${index}]:`, uri)
              }
            })
          } else {
            log.warn(`Input ${key} is not an array:`, requestInputs[key])
          }
        }
      })
      const loaded = await Promise.all(assets)
      log.info('Assets loaded:', loaded)
      loaded.forEach((el) => {
        if (['images', 'videos', 'audios'].includes(flow.inputs[el.input].type)) {
          if (!inputs[el.input]) inputs[el.input] = []
          inputs[el.input][el.index] = {
            uri: el.uri,
            base64: el.base64,
            tag: el.tag,
            position: el.position,
          }
          log.info(
            `Input ${el.input}[${el.index}] set to:`,
            inputs[el.input][el.index]
          )
        } else {
          inputs[el.input] = el.base64
          log.info(`Input ${el.input} set to base64:`, el.base64)
        }
      })
      await ensureUuidReferencesPersisted(store, inputs)

      const socket = store.rootState.io._io.core
      if (!socket) {
        log.err('core io not exists')
        return null
      }
      log.info('Sending comfy:call with inputs:', inputs)
      try {
        const callReply = await socket.emitWithAck('comfy:call', {
          flow,
          inputs,
          project: store.rootState.project.uuid,
        })
        log.info('callReply:', callReply)
        if (
          !callReply ||
          !callReply.success ||
          typeof callReply.uuid !== 'string' ||
          !callReply.uuid
        ) {
          // Backend replies { success: false, error } on task-creation failure;
          // older builds replied success:true with an error object as uuid.
          const message =
            callReply?.error ||
            callReply?.uuid?.error ||
            'Generation failed to start'
          log.err('callTool API error:', message, callReply)
          throw new Error(message)
        }

        const channel = supabase.channel(`task-${callReply.uuid}`)
        channel
          .on('broadcast', { event: 'task' }, ({ payload }) => {
            log.info('task broadcast', callReply.uuid, payload)
            store.dispatch('bctask', { data: payload })
            if (['complete', 'taskError', 'error'].includes(payload.step)) {
              supabase.removeChannel(channel)
            }
          })
          .subscribe()
        log.info('Subscribed to task channel:', `task-${callReply.uuid}`)

        store.commit('addTask', {
          uuid: callReply.uuid,
          flow,
          processing: true,
          complete: false,
          failed: false,
          progress: 0,
          stage: 'queued',
          output: null,
          error: null,
          timestamp: new Date().toISOString(),
        })
        store.commit('project/aiTool', null, { root: true })
        return callReply.uuid
      } catch (error) {
        log.err('WebSocket emit error:', error)
        // Propagate so chat surfaces can show the actual backend error
        // instead of a generic "failed to start" message.
        throw error
      }
    },
    genInit: async (store) => {
      store.commit('on', true)
      await store.dispatch('genQuiz', { frame: {} })
    },
    genQuiz: async (store, { frame, negative = {} }) => {
      const positive = await liquify('llm4sd', {
        env: store.state.project.env,
        frame: {
          ...store.state.project.frame,
          ...(frame || {}),
        },
      })
      const apiReply = await AiApi(store.getters.jwt, '/llm4sd/image', {
        prompt: positive,
      })
      log.info('genQuiz', { apiReply, positive, negative })
      if (!apiReply || !apiReply.success || !apiReply.uuid) {
        log.err('genQuiz API error', apiReply)
        return
      }

      const channel = supabase.channel(`task-${apiReply.uuid}`)
      channel
        .on('broadcast', { event: 'task' }, ({ payload }) => {
          log.info('task broadcast', apiReply.uuid, payload)
          store.dispatch('bctask', { data: payload })
            if (['complete', 'taskError', 'error'].includes(payload.step)) {
            supabase.removeChannel(channel)
          }
        })
        .subscribe()

      store.commit('addTask', {
        uuid: apiReply.uuid,
        processing: true,
        complete: false,
        failed: false,
        progress: 0,
        stage: 'queued',
        output: null,
        error: null,
        timestamp: new Date().toISOString(),
      })
    },
    helpMessage: async (store, { message, mode }) => {
      const socket = store.rootState.io._io.core
      if (!socket) {
        log.err('core io not exists')
        return
      }
      store.commit('helpMessage', { role: 'user', message, mode })
      const reply = await socket.emitWithAck('vyby:chat', { message, mode })
      if (reply) {
        let assistantText = reply
        if (assistantText && typeof assistantText === 'object') {
          if (typeof assistantText.chat === 'string') assistantText = assistantText.chat
          else if (typeof assistantText.message === 'string')
            assistantText = assistantText.message
          else assistantText = JSON.stringify(assistantText)
        }
        if (assistantText) {
          store.commit('helpMessage', { role: 'assistant', message: assistantText })
        }
      }
      log.info('help reply', reply)
    },
    bctask: async (store, { data }) => {
      log.info('bctask raw payload', JSON.stringify(data, null, 2))
      const uuid = data.taskId || data.uuid
      if (!uuid || !store.state.tasks[uuid]) {
        return
      }

      switch (data.step) {
        case 'queued':
          store.commit('taskQueued', {
            uuid,
            output: data.output,
            timestamp: data.timestamp,
          })
          break
        case 'started':
          store.commit('taskStart', {
            uuid,
            output: data.output,
            timestamp: data.timestamp,
          })
          break
        case 'progress':
          store.commit('taskProgress', {
            uuid,
            progress: data.progress || 0,
            stage: data.stage || 'unknown',
            timestamp: data.timestamp,
          })
          break
        case 'complete':
          console.warn('COOOMMMPLETE', data)
          {
            const extracted = extractTaskOutputForUuid(data.output, uuid)
            store.commit('taskComplete', {
              uuid,
              output: extracted,
              inputs: data.inputs,
              timestamp: data?.timestamp,
            })

            // Phase 1: immediately materialize provider outputs as real library assets (UUID-backed)
            // so downstream nodes can safely reference them (UUID -> base64/URL) during generation.
            Promise.resolve()
              .then(() => autoImportTaskOutputsToLibrary(store, extracted, { taskId: uuid }))
              .catch((e) => log.warn('[autoImport] failed', e))
          }
          break
        case 'taskError':
          store.commit('taskError', {
            uuid,
            error: data.error || { message: 'Unknown error' },
            timestamp: data.timestamp,
          })
          break
        default:
          log.warn(`Unhandled task step: ${data.step}`, data)
          break
      }
    },
  },
}
